import React, { useState } from 'react';
import {
    Avatar,
    Button,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Spinner,
    Text,
    Tooltip,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import {
    ArrowDownload16Regular,
    ArrowRepeatAll16Regular,
    Info16Regular,
    Save16Regular,
    ThumbDislike16Filled,
    ThumbDislike16Regular,
    ThumbLike16Filled,
    ThumbLike16Regular,
} from '@fluentui/react-icons';
import { ChatMessage, ChatMessageType, UserFeedback } from '../../../domain/messages/models/ChatMessage';
import { customColors, customFonts } from '../../../styles';
import WordIcon from '../../../assets/icons8-microsoft-word-2019-48.png';
import PowerPointIcon from '../../../assets/icons8-microsoft-powerpoint-2019-48.png';

const MIGRATION_CTX_REGEX = /__MIGRATION_CTX_START__[\s\S]*?__MIGRATION_CTX_END__\n*/;
const stripMigrationContext = (content: string): string => content.replace(MIGRATION_CTX_REGEX, '').trimStart();
import useChatHistoryItem from '../hooks/useChatHistoryItem';
import ChatHistoryTextContent from './ChatHistoryTextContent';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import CopyChatButton from './CopyChatButton';
import ChatMessageInfo from '../../ChatList/components/dialogs/ChatMessageInfo';
import AuthenticatedImage from '../../../shared/ui/AuthenticatedImage';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '95%',
        ...shorthands.margin('0', '0', '10px', '0'),
        ...shorthands.gap('8px'),
    },
    rootUser: {
        alignSelf: 'flex-end',
        flexDirection: 'row-reverse',
    },
    avatar: {
        display: 'block',
        '& > span': {
            ...shorthands.borderRadius('5px'),
            '& > img': {
                backgroundColor: 'transparent',
            },
        },
    },
    userAvatar: {
        order: 1,
    },
    item: {
        minWidth: 0,
        maxWidth: '100%',
        ...shorthands.padding('8px', '16px'),
        ...shorthands.borderRadius('15px'),
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.botMessageBorder),
        position: 'relative',
        ...shorthands.overflow('hidden'),
    },
    me: {
        backgroundColor: customColors.lightGray,
        ...shorthands.border('1px', 'solid', 'transparent'),
    },
    header: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.margin('0', '0', '4px', '0'),
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.Lato,
    },
    headerMeta: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        minHeight: '20px',
    },
    author: {
        fontWeight: 600,
        fontSize: '14px',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.Lato,
    },
    time: {
        fontFamily: customFonts.Lato,
        fontSize: '11px',
        fontWeight: 400,
        color: 'rgba(0,0,0,0.5)',
    },
    timeBot: {
        paddingLeft: '12px',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('2px'),
    },
    actionButton: {
        minWidth: 'unset',
        ...shorthands.border('none'),
        backgroundColor: 'transparent',
        color: 'rgba(0,0,0,0.7)',
        ...shorthands.borderRadius('5px'),
        paddingTop: '1px',
        paddingBottom: '1px',
        paddingLeft: '3px',
        paddingRight: '3px',
        ':hover': {
            backgroundColor: '#f2f2f2',
            color: customColors.summarizeButtonDarker,
            transition: 'background-color 0.3s ease-in-out',
        },
        ':disabled': {
            color: 'rgba(0,0,0,0.35)',
            backgroundColor: 'transparent',
        },
    },
    disclaimerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('8px'),
        ...shorthands.margin('8px', '0', '0', '0'),
        width: '100%',
    },
    feedbackActions: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('4px'),
        marginLeft: '8px',
        flexShrink: 0,
    },
    disclaimer: {
        fontSize: '11px',
        lineHeight: '1.4',
        color: 'rgba(0,0,0,0.5)',
        fontFamily: customFonts.Lato,
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        maxWidth: 'none',
        width: 'auto',
        flex: '1 1 auto',
        paddingTop: '8px',
        textAlign: 'start',
        minWidth: '240px',
    },
    messageContent: {
        minWidth: 0,
        maxWidth: '100%',
    },
    pendingStatusRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        paddingTop: '4px',
        paddingBottom: '4px',
    },
    pendingPulseRing: {
        position: 'relative',
        flexShrink: 0,
        width: '10px',
        height: '10px',
    },
    pendingPulseRingExit: {
        animationName: {
            from: { opacity: '1', transform: 'scale(1)' },
            to: { opacity: '0', transform: 'scale(0)', width: '0px', height: '0px' },
        },
        animationDuration: '0.25s',
        animationTimingFunction: 'ease-in',
        animationFillMode: 'forwards',
        pointerEvents: 'none',
    },
    pendingPulseCore: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '7px',
        height: '7px',
        ...shorthands.borderRadius('50%'),
        backgroundColor: customColors.protivitiDarkBlue,
        animationName: {
            '0%, 100%': { opacity: '0.6', transform: 'translate(-50%, -50%) scale(0.9)' },
            '50%': { opacity: '1', transform: 'translate(-50%, -50%) scale(1.1)' },
        },
        animationDuration: '2s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
    },
    pendingPulseWave: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '7px',
        height: '7px',
        ...shorthands.borderRadius('50%'),
        ...shorthands.border('1.5px', 'solid', customColors.protivitiOrange),
        animationName: {
            '0%': { opacity: '0.6', transform: 'translate(-50%, -50%) scale(1)' },
            '100%': { opacity: '0', transform: 'translate(-50%, -50%) scale(2.5)' },
        },
        animationDuration: '2s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-out',
    },
    pendingStatusText: {
        fontSize: '13px',
        fontWeight: 400,
        fontFamily: customFonts.Lato,
        backgroundImage: `linear-gradient(90deg, ${customColors.protivitiDarkBlue} 0%, ${customColors.protivitiOrange} 40%, ${customColors.protivitiDarkBlue} 80%)`,
        backgroundSize: '200% auto',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        animationName: [
            { from: { backgroundPosition: '200% center' }, to: { backgroundPosition: '0% center' } },
            { from: { opacity: '0' }, to: { opacity: '1' } },
        ],
        animationDuration: '2.5s, 0.3s',
        animationTimingFunction: 'linear, ease-out',
        animationIterationCount: 'infinite, 1',
        animationFillMode: 'none, both',
    },
    singleCitationLink: {
        textDecorationLine: 'none',
        color: customColors.buttonTextDarkBlue,
    },
    imagesRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '8px',
        marginTop: '8px',
        overflowX: 'auto',
        width: 'fit-content',
        maxWidth: '100%',
        '::-webkit-scrollbar': {
            height: '4px',
        },
        '::-webkit-scrollbar-thumb': {
            ...shorthands.borderRadius('2px'),
            backgroundColor: customColors.botMessageBorder,
        },
    },
    messageImage: {
        width: '180px',
        height: '135px',
        flexShrink: 0,
        ...shorthands.borderRadius('6px'),
        objectFit: 'cover',
        display: 'block',
    },
    citationsRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: '10px',
        ...shorthands.gap('8px'),
    },
    documentsReferenced: {
        marginTop: '0px',
        marginBottom: '0px',
        fontSize: '12px',
        fontWeight: '700',
        fontFamily: customFonts.Lato,
    },
    singleCitation: {
        marginBottom: '0px',
        fontSize: '12px',
        fontWeight: '500',
        fontFamily: customFonts.Lato,
        ...shorthands.padding('1px', '7px'),
        ...shorthands.borderRadius('8px'),
        backgroundColor: customColors.lightBlueGrayBackground2,
        color: customColors.buttonTextDarkBlue,
    },
});

export interface ChatHistoryItemProps {
    message: ChatMessage;
    messageIndex: number;
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, messageIndex }) => {
    const styles = useStyles();
    const vm = useChatHistoryItem({ message, messageIndex });
    const { isPending, isStreaming, pendingStatusText } = vm;
    const [showMessageInfo, setShowMessageInfo] = useState(false);

    // Don't render empty bot messages that aren't actively being generated
    if (vm.isBot && !message.content && !isPending) {
        return null;
    }
    const uniqueCitations = message.citations
        ? message.citations.filter(
            (citation, index, array) =>
                array.findIndex((c) => c.link === citation.link && c.title === citation.title) === index,
        )
        : message.citations;
    const citationsItems =
        uniqueCitations && uniqueCitations.length > 3 ? uniqueCitations : uniqueCitations?.slice(0, 3);


    return (
        <div className={mergeClasses(styles.root, vm.isUser ? styles.rootUser : undefined)}>
            <Avatar
                className={mergeClasses(styles.avatar, vm.isUser ? styles.userAvatar : undefined)}
                name={vm.authorName}
                image={vm.avatarSrc ? { src: vm.avatarSrc } : undefined}
                shape="square"
            />
            <div className={mergeClasses(styles.item, vm.isUser ? styles.me : undefined)}>
                <div className={styles.header}>
                    <div className={styles.headerMeta}>
                        {vm.isBot && <Text className={styles.author}>{vm.authorName}</Text>}
                        {!isPending && (
                            <Text className={mergeClasses(styles.time, vm.isBot ? styles.timeBot : undefined)}>
                                {vm.timestampText}
                            </Text>
                        )}
                    </div>
                    {!isPending && (
                        <div className={styles.actions}>
                            {vm.showInfoButton && (
                                <Tooltip content={vm.disclaimerText} relationship="description">
                                    <Button
                                        className={styles.actionButton}
                                        icon={<Info16Regular />}
                                        aria-label="info"
                                        onClick={() => setShowMessageInfo((prev) => !prev)}
                                    />
                                </Tooltip>
                            )}
                            {vm.showCopyButton && <CopyChatButton message={stripMigrationContext(message.content)} />}
                            {vm.showSaveButton && (
                                <Tooltip content="Save prompt" relationship="label">
                                    <Button
                                        className={styles.actionButton}
                                        icon={<Save16Regular />}
                                        onClick={() => void vm.onSavePrompt()}
                                        aria-label="save-prompt"
                                    />
                                </Tooltip>
                            )}
                            {vm.showDownloadButton && vm.isCurrentMessageExportingToPptx && <Spinner size="tiny" />}
                            {vm.showDownloadButton && !vm.isCurrentMessageExportingToPptx && (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <Tooltip content="Export" relationship="label">
                                            <Button
                                                className={styles.actionButton}
                                                icon={<ArrowDownload16Regular />}
                                                aria-label="download"
                                            />
                                        </Tooltip>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem onClick={vm.onDownloadAsWord}>
                                                <img
                                                    src={WordIcon}
                                                    alt="Word"
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        marginRight: 8,
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                                Word
                                            </MenuItem>
                                            <MenuItem onClick={vm.onDownloadAsPptx}>
                                                <img
                                                    src={PowerPointIcon}
                                                    alt="PowerPoint"
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        marginRight: 8,
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                                PowerPoint
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            )}
                            {vm.showRegenerateButton && (
                                <Tooltip content="Regenerate" relationship="label">
                                    <Button
                                        className={styles.actionButton}
                                        icon={<ArrowRepeatAll16Regular />}
                                        onClick={vm.onRegenerate}
                                        disabled={!vm.isLatestBotMessage}
                                        aria-label="regenerate"
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
                <div id={vm.messageContentElementId} className={styles.messageContent}>
                    {isPending ? (
                        <div className={styles.pendingStatusRow}>
                            <div
                                className={mergeClasses(
                                    styles.pendingPulseRing,
                                    !!pendingStatusText && styles.pendingPulseRingExit,
                                )}
                            >
                                <span className={styles.pendingPulseCore} />
                                <span className={styles.pendingPulseWave} />
                            </div>
                            {pendingStatusText && (
                                <Text key={pendingStatusText} className={styles.pendingStatusText}>
                                    {pendingStatusText}
                                </Text>
                            )}
                        </div>
                    ) : message.type === ChatMessageType.Message ? (
                        <div>
                            <ChatHistoryTextContent
                                content={stripMigrationContext(message.content)}
                                isBot={vm.isBot}
                                isStreaming={isStreaming}
                            />
                            {vm.isUser && message.images && message.images.length > 0 && (
                                <div className={styles.imagesRow}>
                                    {message.images.map((src, i) => (
                                        <AuthenticatedImage
                                            key={i}
                                            src={src}
                                            alt="user uploaded"
                                            className={styles.messageImage}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <ChatHistoryDocumentContent isUser={vm.isUser} message={message} />
                        </div>
                    )}
                </div>
                {vm.isBot && message.citations && message.citations.length > 0 && (
                    <>
                        <div className={styles.citationsRow}>
                            <p className={styles.documentsReferenced}> {'Sources :'} </p>
                            {citationsItems?.map((citation, idx) => (
                                <div key={`${citation.title}_${idx}`} className={styles.singleCitation}>
                                    {!citation.link || citation.link == '' ? (
                                        <span>{citation.title}</span>
                                    ) : (
                                        <a
                                            className={styles.singleCitationLink}
                                            href={citation.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {citation.title}
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {!isPending && vm.showDisclaimer && (
                    <div className={styles.disclaimerRow}>
                        <Text className={styles.disclaimer}>{vm.disclaimerText}</Text>
                        {vm.showFeedbackButtons && (
                            <div className={styles.feedbackActions}>
                                <Tooltip content="Like" relationship="label">
                                    <Button
                                        className={styles.actionButton}
                                        icon={
                                            vm.feedbackValue === UserFeedback.Positive ? (
                                                <ThumbLike16Filled />
                                            ) : (
                                                <ThumbLike16Regular />
                                            )
                                        }
                                        onClick={vm.onTogglePositiveFeedback}
                                        aria-label="like"
                                    />
                                </Tooltip>
                                <Tooltip content="Dislike" relationship="label">
                                    <Button
                                        className={styles.actionButton}
                                        icon={
                                            vm.feedbackValue === UserFeedback.Negative ? (
                                                <ThumbDislike16Filled />
                                            ) : (
                                                <ThumbDislike16Regular />
                                            )
                                        }
                                        onClick={vm.onToggleNegativeFeedback}
                                        aria-label="dislike"
                                    />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                )}
                {
                    showMessageInfo && <ChatMessageInfo
                        message={message}
                        onClose={() => setShowMessageInfo(false)}
                    />
                }
            </div>
        </div>
    );
};

export default ChatHistoryItem;
